<template>
  <div>
    <div
      v-if="loading"
      class="loading"
    >
      <i class="icon fa-solid fa-spinner fa-spin"></i>
    </div>

    <template v-if="ready">
      <router-view></router-view>
    </template>

    <!-- Video Element Upsell -->
    <modal
      v-if="permissionWarning === 'video'"
      class="text-center"
      auto-height
      close-button
      @close="$store.dispatch('setPermissionWarning', null)"
    >
      <template #modal-body>
        <h1 class="margin-bottom-md">
          <i class="fas fa-bolt"></i>
        </h1>

        <h4 class="margin-bottom-md upsell-title">
          Unleash the power of Videos
        </h4>

        <div class="margin-bottom-md upsell-message">
          <p>
            Now make your productions even more powerful when you upload videos
            to your graphics.
          </p>

          <a
            href="https://landing.tagboard.com/video-upload"
            target="_blank"
            rel="noreferrer noopener"
            class="upsell-url"
          >
            How Can I Add Video to My Account
          </a>
        </div>
      </template>
    </modal>
    <!-- End Video Element Upsell -->

    <!-- Data Provider Upsell -->
    <modal
      v-else-if="permissionWarning"
      class="text-center"
      auto-height
      close-button
      @close="$store.dispatch('setPermissionWarning', null)"
    >
      <template #modal-body>
        <h4 class="margin-bottom-md upsell-title">
          So You Want To Add More
          <br />
          Data Providers, Hmm?
        </h4>

        <div class="margin-bottom-md upsell-message">
          <p>Great! We can help you out.</p>

          <a
            href="https://landing.tagboard.com/data-add-request"
            target="_blank"
            rel="noreferrer noopener"
            class="upsell-url"
          >
            I'd Like To Add More Data Providers
          </a>
        </div>
      </template>
    </modal>
    <!-- End Data Provider Upsell -->

    <!-- QR Code Scan Limit Warning Modal -->
    <modal
      v-if="showScanLimitWarning"
      class="text-center"
      auto-height
      close-button
      @close="$store.dispatch('setShowScanLimitWarning', false)"
    >
      <template #modal-body>
        <h4 class="margin-bottom-md upsell-title">
          You've Reached Your
          <br />
          QR Code Scan Limit
        </h4>

        <div class="margin-bottom-md upsell-message">
          <p>No worries, friend, we can help you out.</p>

          <a
            href="https://landing.tagboard.com/qr-code-scan-limit-increase"
            target="_blank"
            rel="noreferrer noopener"
            class="upsell-url"
          >
            Request More Scans For My Account
          </a>
        </div>
      </template>
    </modal>
    <!-- End QR Code Scan Limit Warning Modal -->
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import ConduitEventsService from '../services/conduit';
import { inIframe } from '../utils/helpers';

import Modal from './Modal/Modal.vue';

const getUserIdQueryParam = () => {
  const query = new URLSearchParams(window.location.search);
  return query.get('user_id');
};

export default {
  components: {
    Modal,
  },

  data() {
    return {
      ready: false,
      userFonts: [],
    };
  },

  computed: {
    ...mapState({
      loading: (state) => state.templates.loading,
      customFonts: (state) => state.user.customFonts,
      plan: (state) => state.user.plan,
      showScanLimitWarning: (state) => state.user.showScanLimitWarning,
      permissionWarning: (state) => state.user.permissionWarning,
      userId: (state) => state.user.user?.id,
    }),

    ...mapGetters([
      'hasSportradarElement',
    ]),

    needsApiKeys() {
      // Add checks for other types that need keys here.
      // For image keys only, base league permissions are set in the user plan.
      return this.hasSportradarElement;
    },
  },

  watch: {
    customFonts(newVal) {
      if (newVal) {
        this.startLoader();
        this.attachCustomFont();
        this.ready = true;
        this.$store.dispatch('stopLoader');
      }
    },
  },

  mounted() {
    // Listen for popstate events, and bubble up to any parent (if inside an iframe).
    // But only if we went _back_ to /create.
    if (inIframe()) {
      window.addEventListener('popstate', this.onPopState);

      // Click listener because we preventdefault on a lot of things (like selecting elements)
      // and need to get the parent to focus the iframe manually in tgb-app
      window.addEventListener('click', this.notifyParentOfClick);
    }
  },

  beforeUnmount() {
    if (inIframe()) {
      window.removeEventListener('popstate', this.onPopState);
      window.removeEventListener('click', this.notifyParentOfClick);
    }
  },

  async created() {
    this.startLoader();

    // Set owner based on query params. If not provided, will default to logged-in
    // user, which will most likely not be expected for child accounts.
    // Also check for legacy `user_id` query param _before_ the hash routing.
    // That check can be removed once `tgb-app` is updated to use new routes.
    if (this.$route.query.owner) {
      this.$store.dispatch('setOwner', +this.$route.query.owner);
    } else if (getUserIdQueryParam()) {
      this.$store.dispatch('setOwner', +getUserIdQueryParam());
    }

    // Make sure we load user + account owner's plan before all else
    await this.$store.dispatch('getInfo');
    await this.$store.dispatch('getUserAccounts');
    await this.$store.dispatch('getPermissions');

    const { custom_fonts: hasCustomFonts, qr_code_scans: qrCodeScans } = this.plan.permissions;

    await Promise.all([
      this.$store.dispatch('getUserSnippets'),
      this.needsApiKeys ? this.$store.dispatch('getUserApiKeys') : Promise.resolve(),
    ]).catch((err) => {
      // eslint-disable-next-line no-console
      console.error(err);
    });

    // Stop the loader. The rest can be loaded async
    this.ready = true;
    this.$store.dispatch('stopLoader');

    await ConduitEventsService.init(this.userId);

    // Load stats and fonts after making sure user accounts are loaded
    // Otherwise, owner won't be set yet
    await Promise.all([
      // Only need to fetch total scans if user has a limit on scans
      qrCodeScans > 0 ? this.$store.dispatch('getQRCodeScans') : true,

      // Only load fonts if user has custom_fonts permission
      hasCustomFonts ? this.$store.dispatch('getFonts') : true,
    ]).catch((err) => {
      // eslint-disable-next-line no-console
      console.log(err);
    });

    if (hasCustomFonts) {
      this.attachCustomFont();
    }
  },

  methods: {
    ...mapActions([
      'startLoader',
    ]),

    onPopState() {
      try {
        if (this.$route.name === 'create') {
          window.parent.postMessage('tgbGraphics:popstate', '*');
        }
      } catch (err) { /* noop */ }
    },

    notifyParentOfClick() {
      try {
        window.parent.postMessage('tgbGraphics:click', '*');
      } catch (err) { /* noop */ }
    },

    async attachCustomFont() {
      // Loop through the array to extract only the media_urls
      this.customFonts.forEach((element) => {
        this.userFonts.push({
          font: element.title.split('.')[0],
          url: element.alt ? element.alt.media_url : element.media_url,
          fontId: `f-${element._id}`,
        });
      });

      await this.createFontLinks();
    },

    createFontLinks() {
      let css = '';

      this.userFonts.forEach((font) => {
        const fontFace = `
        @font-face {
          font-family: '${font.fontId}';
          src: url('${font.url}');
          font-weight: bold;
          font-style: normal;
          font-display: block;
        }

      `;

        css += fontFace;
      });

      const styleEl = document.createElement('style');
      styleEl.id = 'customFonts';
      styleEl.innerHTML = css;

      document.head.appendChild(styleEl);
    },
  },
};
</script>
